import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { APP_QUERY_KEYS } from '@/lib/constants/app';
import { BUILD_READINESS_QUERY_KEYS } from '@/lib/constants/buildReadiness';
import { AppApi } from './AppApi';
import { AppUtils } from './AppUtils';
import type {
  AppCfgCreateReq,
  AppCfgWebCreateReq,
  AppCfgWebUpdateReq,
  AppCfgRokuCreateReq,
  AppCfgRokuUpdateReq,
  AppCfgPlatformCreateReq,
  AppCfgPlatformUpdateReq,
  AppCfgListingUpsertReq,
  AppCfgsDto,
} from '@/lib/types/App';
import type { AppPlatformKey } from '@/lib/constants/appPlatform';

export function useCreateApp() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgCreateReq) => AppApi.createApp(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
    },
  });
}

export function useDeleteApp() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appCfgId: number) => AppApi.deleteApp(selectedCId!, appCfgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
    },
  });
}

export function useToggleApp() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appCfgId,
      currentApp,
    }: {
      appCfgId: number;
      currentApp: AppCfgsDto;
    }) => {
      const req = AppUtils.buildToggleUpdateReq(currentApp);
      return AppApi.updateApp(selectedCId!, appCfgId, req);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, variables.appCfgId),
      });
    },
  });
}

export function useUpdateApp(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: Parameters<typeof AppApi.updateApp>[2]) =>
      AppApi.updateApp(selectedCId!, appCfgId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
    },
  });
}

/**
 * The home background switch (design ADR-0015 D2), saved at once through the
 * app update. The re-read of the app is awaited INSIDE the mutation, so it
 * stays pending until the stored value is on screen — the switch never
 * flickers back between the save and the refetch.
 */
export function useSetHomeBackground(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ app, enabled }: { app: AppCfgsDto; enabled: boolean }) =>
      AppApi.updateApp(selectedCId!, appCfgId, AppUtils.buildHomeBgUpdateReq(app, enabled)),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) }),
        queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId) }),
      ]),
  });
}

export function useCreateAppWeb(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgWebCreateReq) => AppApi.createAppWeb(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
      // The address and the platform rows are build prerequisites: the Readiness card re-asks (3.34.5).
      queryClient.invalidateQueries({ queryKey: BUILD_READINESS_QUERY_KEYS.app(selectedCId!, appCfgId) });
    },
  });
}

export function useUpdateAppWeb(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgWebUpdateReq) =>
      AppApi.updateAppWeb(selectedCId!, appCfgId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
      // The address and the platform rows are build prerequisites: the Readiness card re-asks (3.34.5).
      queryClient.invalidateQueries({ queryKey: BUILD_READINESS_QUERY_KEYS.app(selectedCId!, appCfgId) });
    },
  });
}

export function useCreateAppRoku(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgRokuCreateReq) => AppApi.createAppRoku(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
      // The address and the platform rows are build prerequisites: the Readiness card re-asks (3.34.5).
      queryClient.invalidateQueries({ queryKey: BUILD_READINESS_QUERY_KEYS.app(selectedCId!, appCfgId) });
    },
  });
}

export function useUpdateAppRoku(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgRokuUpdateReq) =>
      AppApi.updateAppRoku(selectedCId!, appCfgId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
      // The address and the platform rows are build prerequisites: the Readiness card re-asks (3.34.5).
      queryClient.invalidateQueries({ queryKey: BUILD_READINESS_QUERY_KEYS.app(selectedCId!, appCfgId) });
    },
  });
}

export function useCreateAppPlatform(appCfgId: number, platform: AppPlatformKey) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgPlatformCreateReq) =>
      AppApi.createAppPlatform(selectedCId!, platform, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
      // The address and the platform rows are build prerequisites: the Readiness card re-asks (3.34.5).
      queryClient.invalidateQueries({ queryKey: BUILD_READINESS_QUERY_KEYS.app(selectedCId!, appCfgId) });
    },
  });
}

export function useUpdateAppPlatform(appCfgId: number, platform: AppPlatformKey) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgPlatformUpdateReq) =>
      AppApi.updateAppPlatform(selectedCId!, platform, appCfgId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
      // The address and the platform rows are build prerequisites: the Readiness card re-asks (3.34.5).
      queryClient.invalidateQueries({ queryKey: BUILD_READINESS_QUERY_KEYS.app(selectedCId!, appCfgId) });
    },
  });
}

export function useUpsertAppListing(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: AppCfgListingUpsertReq) =>
      AppApi.upsertAppListing(selectedCId!, appCfgId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) });
      queryClient.invalidateQueries({
        queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId),
      });
    },
  });
}

/**
 * Debounced availability check for web app custom name.
 * Uses useQuery with enabled gate — caller controls `customName` reactively.
 * Only fires when customName meets minimum length.
 */
export function useCheckCustomNameAvailability(customName: string) {
  const { selectedCId } = useAuth();
  const trimmed = customName.trim().toLowerCase();

  return useQuery({
    queryKey: APP_QUERY_KEYS.customNameCheck(selectedCId ?? 0, trimmed),
    queryFn: () => AppApi.checkCustomNameAvailability(selectedCId!, trimmed),
    enabled: selectedCId != null && trimmed.length >= 2,
    staleTime: 30_000,
  });
}
